import { useContext } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { Button, Modal, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { apiRequest } from 'helpers/api';

import { HeadquarterContext } from 'context/HeadquarterContext';

import { NOTIFICATION_VARIANTS } from 'constants/chownow';

interface DisableMiddlewareProps {
  onDisableMiddleware?: () => void;
}

interface ApiResponse {
  status: number;
}

export default function DisableMiddleware({ onDisableMiddleware }: DisableMiddlewareProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const { location } = useContext(HeadquarterContext);
  const { id, middleware_integrator: middleware, name } = location;

  const showErrorNotification = () => {
    notifications.show({
      message: `Failed to disable ${middleware}`,
      variant: NOTIFICATION_VARIANTS.caution.label,
      icon: NOTIFICATION_VARIANTS.caution.icon,
      style: { marginTop: 'var(--mantine-spacing-sm)' },
    });
  };

  const handleDisableMiddleware = async () => {
    try {
      const { data } = await apiRequest<ApiResponse>({
        path: `restaurant/${id}/disable-middleware-integration`,
        method: 'POST',
      });

      if (data?.status === 200) {
        onDisableMiddleware?.();
        notifications.show({
          message: `Successfully disabled ${middleware}`,
          variant: NOTIFICATION_VARIANTS.success.label,
          icon: NOTIFICATION_VARIANTS.success.icon,
          style: { marginTop: 'var(--mantine-spacing-sm)' },
        });
      } else {
        showErrorNotification();
      }
    } catch {
      showErrorNotification();
    } finally {
      close();
    }
  };

  return (
    <>
      <Button variant="link" onClick={open} style={{ alignSelf: 'center' }}>
        Disable {middleware}
      </Button>
      <Modal opened={opened} onClose={close} title={`Disable ${middleware}`}>
        <Text>
          This will mark <b>{name}</b> as inactive on <b>{middleware}</b> and revoke all access
          tokens. Are you sure you want to disable?{' '}
        </Text>
        <Button
          fullWidth
          mt="md"
          onClick={() => {
            void handleDisableMiddleware();
          }}
        >
          Confirm
        </Button>
        <Button fullWidth mt="xs" onClick={close} variant="outline">
          Cancel
        </Button>
      </Modal>
    </>
  );
}
